import { siteHasFeature, type SiteFeature } from "./features";

/** The workspace is one site in several areas. The Website is always there; the Blog is there once the site has one. */
export type Area = "website" | "blog" | "shop";

/** An area the site has, as the switcher lists it. */
export interface AreaRow {
  id: Area;
  name: string;
  description: string;
  /** A small amber note on the row, e.g. "3 not live". */
  note?: string;
}

/** An area the site could add, shown below the ones it has. */
export interface AddRow {
  feature: SiteFeature;
  name: string;
  description: string;
}

export interface AreaState {
  site: { features: readonly string[] };
  /** The blog's own name, or null while the site has no blog. */
  blog: { name: string; enabled: boolean } | null;
  /** The Website's changes that aren't live yet. */
  shop?: { name: string } | null;
  websiteNotLive: number;
  current: Area;
}

/**
 * What a site can add, keyed by the platform feature that allows it. The shop's slice adds its own entry here
 * (`{ feature: "shop", name: "Add a shop", description: "Sell products and downloads", has: … }`) and nothing else in the switcher changes.
 */
interface AddEntry extends AddRow {
  /** True once the site has this area. */
  has: (state: AreaState) => boolean;
}

const ADD_ENTRIES: readonly AddEntry[] = [
  { feature: "shop", name: "Add a shop", description: "Sell products and downloads", has: (state) => Boolean(state.shop) },
  { feature: "blog", name: "Add a blog", description: "Write posts on your site", has: (state) => state.blog !== null },
];

export interface SwitcherRows {
  areas: AreaRow[];
  adds: AddRow[];
  /** True when there is an area to switch to or add. The switcher is always in the drawer (Site settings lives in it), but with one area and nothing to add it has no areas to offer, so the Ctrl+digit shortcuts stay off. */
  visible: boolean;
}

export function switcherRows(state: AreaState): SwitcherRows {
  const areas: AreaRow[] = [{
    id: "website", name: "Website", description: "Design, pages and Publish",
    ...(state.current !== "website" && state.websiteNotLive > 0 ? { note: `${state.websiteNotLive} not live` } : {}),
  }];
  if (state.blog) areas.push({ id: "blog", name: "Blog", description: state.blog.enabled ? "Posts, tags and settings" : "Off · posts are kept" });
  if (state.shop) areas.push({ id: "shop", name: "Shop", description: "Products, categories, orders and settings" });
  const adds = ADD_ENTRIES.filter((entry) => siteHasFeature(state.site, entry.feature) && !entry.has(state)).map(({ feature, name, description }) => ({ feature, name, description }));
  return { areas, adds, visible: areas.length > 1 || adds.length > 0 };
}

/** The area a Ctrl+digit shortcut switches to: Ctrl+1 is the first row, Ctrl+2 the second. Null when it isn't one. */
export function areaForShortcut(event: { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean; shiftKey: boolean }, areas: readonly AreaRow[]): Area | null {
  if (!event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return null;
  const id = ({ "1": "website", "2": "blog", "3": "shop" } as const)[event.key as "1" | "2" | "3"];
  return areas.find((area) => area.id === id)?.id ?? null;
}

/** The hint under the menu: "Switch with Ctrl+1 or 2". Null with only one area. */
export function shortcutHint(areas: readonly AreaRow[]): string | null {
  return areas.length > 1 ? `Switch with Ctrl+1 or ${areas.some((area) => area.id === "shop") ? 3 : 2}` : null;
}

/** The two lines of the switcher button: the area's name, then what it is. */
export function switcherLabel(area: Area, blog: { name: string } | null, address: string, shop?: { name: string } | null): { name: string; detail: string } {
  if (area === "shop") return { name: "Shop", detail: [shop?.name, address].filter(Boolean).join(" · ") };
  return area === "blog" ? { name: "Blog", detail: [blog?.name, address].filter(Boolean).join(" · ") } : { name: "Website", detail: address };
}
